'use client';

import {
  Modal,
  Card,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  CardBody,
  CardHeader,
  Accordion,
  AccordionItem,
} from '@nextui-org/react';
import {
  ChevronLeft,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  MinusCircle,
} from 'lucide-react';
import Image from 'next/image';
import { useForm } from 'react-hook-form';

import {
  useApproveReviewMutation,
  useRejectReviewMutation,
} from '@/entities/admin/review/query';
import { IReview, ReviewStatusEnum } from '@/entities/review/types';
import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { parseDate } from '@/shared/utils/parseDate';
import { cn } from '@/shared/utils/utils';
import { Rate } from '@/components/ui/rate';
import { Textarea } from '@nextui-org/input';

export const ReviewCard = ({ review }: { review?: IReview }) => {
  if (!review) {
    return null;
  }
  return (
    <Card>
      <CardHeader className="text-lg pb-0">
        {review.masterclass?.name}
      </CardHeader>
      <CardBody className="pt-0 overflow-hidden">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <div className="pb-1">
              <ReviewStatus status={review.status} />
            </div>
            <div className="flex gap-2">
              <Rate value={review.rate} readOnly />
              <div className="text-gray-600">{parseDate(review.createdAt)}</div>
            </div>
          </div>
          <div className="w-[80px] h-[80px] rounded-xl overflow-hidden">
            <Image
              src={review.masterclass?.images?.[0].url}
              alt={''}
              width={80}
              height={80}
            />
          </div>
        </div>
        <div>{review.description}</div>
        {review.status === ReviewStatusEnum.REJECTED && (
          <Accordion className="bg-red-300 rounded-lg overflow-hidden mt-2">
            <AccordionItem
              indicator={
                <ChevronLeft
                  className="text-gray-900"
                  strokeWidth={2}
                  size={24}
                />
              }
              key="1"
              className="bg-red-300 text-gray-900"
              aria-label="Accordion 1"
              title="Причина отклонения"
            >
              <p>{review.rejectReason}</p>
              <p>
                Ознакомтесь с{' '}
                <Link
                  className="underline text-blue-500"
                  href={ROUTES['reviews-rules']}
                >
                  правилами написания
                </Link>{' '}
                отзывов
              </p>
            </AccordionItem>
          </Accordion>
        )}
        <div className="flex gap-1 flex-wrap">
          {review.images?.map((imageSrc) => (
            <div
              key={imageSrc}
              className="w-[80px] h-[80px] rounded-xl overflow-hidden"
            >
              <Image
                src={imageSrc}
                alt={`Фото к отзыву на мастер-класс «${review.masterclass?.name}»`}
                width={80}
                height={80}
              />
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

export const RejectReviewModalConfirm = ({
  reviewId,
  close,
  isOpen,
  onOpenChange,
  title = 'Отклонение отзыва',
  submitLabel = 'Отклонить',
}: {
  onOpenChange: () => void;
  close: () => void;
  isOpen: boolean;
  reviewId: number;
  title?: string;
  submitLabel?: string;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ rejectReason: string }>();
  const { mutateAsync: reject, isPending: isRejecting } =
    useRejectReviewMutation();
  const onFinish = async (values: { rejectReason: string }) => {
    await reject({ reviewId, rejectReason: values.rejectReason });
    reset();
    close();
  };

  return (
    <Modal onOpenChange={onOpenChange} isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <div className="mb-2">
            Укажите причину отклонения отзыва. Его сможет увидеть пользователь в
            своем личном кабинете. Также в отклоненном отзыве пользователь
            увидит <Link href={ROUTES['reviews-rules']}>ссылку</Link> на правила
            написания отзывов.
          </div>
          <form onSubmit={handleSubmit(onFinish)} className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-foreground-500">
                Причина отклонения
              </label>
              <Textarea
                placeholder="Введите причину отклонения"
                {...register('rejectReason', {
                  required: 'Пожалуйста, введите причину!',
                })}
              />
              {errors.rejectReason && (
                <span className="text-danger text-sm">
                  {errors.rejectReason.message}
                </span>
              )}
            </div>
            <div className="w-full flex flex-col md:flex-row justify-end items-center gap-2">
              <Button
                className="w-full md:w-auto"
                size="lg"
                onClick={close}
              >
                Отмена
              </Button>
              <Button
                isLoading={isRejecting}
                className="w-full md:w-auto"
                size="lg"
                color="danger"
                type="submit"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const ApproveReviewModalConfirm = ({
  reviewId,
  onOpenChange,
  isOpen,
  close,
}: {
  onOpenChange: () => void;
  close: () => void;
  isOpen: boolean;
  reviewId: number;
}) => {
  const { mutateAsync: approve, isPending: isApproving } =
    useApproveReviewMutation();
  const onFinish = async (e: React.FormEvent) => {
    e.preventDefault();
    await approve({ reviewId });
    close();
  };
  return (
    <Modal onOpenChange={onOpenChange} isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          Вы уверены, что хотите опубликовать этот отзыв?
        </ModalHeader>
        <ModalBody>
          <form onSubmit={onFinish}>
            <div className="w-full flex-col md:flex-row mt-3 flex justify-end items-center gap-2">
              <Button
                size="lg"
                className="w-full md:w-auto"
                onClick={close}
              >
                Отмена
              </Button>
              <Button
                isLoading={isApproving}
                size="lg"
                className="w-full md:w-auto"
                color="primary"
                type="submit"
              >
                Опубликовать
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const Tag = ({
  children,
  color,
  className,
}: {
  children: React.ReactNode;
  color: 'processing' | 'success' | 'warning' | 'error' | 'default';
  className?: string;
}) => {
  const colorClasses: Record<typeof color, string> = {
    processing: 'bg-blue-50 text-blue-600 border-blue-200',
    success: 'bg-green-50 text-green-600 border-green-200',
    warning: 'bg-orange-50 text-orange-600 border-orange-200',
    error: 'bg-red-50 text-red-600 border-red-200',
    default: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs',
        colorClasses[color],
        className,
      )}
    >
      {children}
    </span>
  );
};

export const ReviewStatus = ({
  status,
  className,
}: {
  status: ReviewStatusEnum;
  className?: string;
}) => {
  const iconSize = 14;
  switch (status) {
    case ReviewStatusEnum.PENDING:
      return (
        <Tag className={className} color="processing">
          <Clock size={iconSize} /> На модерации
        </Tag>
      );
    case ReviewStatusEnum.APPROVED:
      return (
        <Tag className={className} color="success">
          <CheckCircle2 size={iconSize} /> Опубликован
        </Tag>
      );
    case ReviewStatusEnum.REJECTED:
      return (
        <Tag className={className} color="warning">
          <AlertCircle size={iconSize} /> Отклонен
        </Tag>
      );
    case ReviewStatusEnum.DELETED:
      return (
        <Tag className={className} color="error">
          <XCircle size={iconSize} /> Удален
        </Tag>
      );
    default:
      return (
        <Tag className={className} color="default">
          <MinusCircle size={iconSize} />
          Без статуса
        </Tag>
      );
  }
};
